import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/site/Logo";
import {
  ShieldCheck,
  LockKey,
  Certificate,
  WhatsappLogo,
  LinkedinLogo,
  FacebookLogo,
  InstagramLogo,
} from "@phosphor-icons/react";

const SOCIAL_LINKS = [
  { href: "https://whatsapp.com/channel/0029VbCnQzl4NVilEQdRqq17", label: "WhatsApp channel", Icon: WhatsappLogo, testId: "footer-whatsapp" },
  { href: "https://www.linkedin.com/company/taxfreefiler/", label: "LinkedIn", Icon: LinkedinLogo, testId: "footer-linkedin" },
  { href: "https://www.facebook.com/share/1KP3cGa9Pp/?mibextid=wwXIfr", label: "Facebook", Icon: FacebookLogo, testId: "footer-facebook" },
  { href: "https://www.instagram.com/taxfreefiler?igsh=MTFsMDVhODdzNm5ucA%3D%3D&utm_source=qr", label: "Instagram", Icon: InstagramLogo, testId: "footer-instagram" },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-[#0A2540] text-slate-100" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-1">
            <div className="bg-white rounded-md p-4 inline-block">
              <Logo size={44} />
            </div>
            <p className="mt-6 text-sm text-slate-300 leading-relaxed">
              Boutique tax operations for individuals, CPA firms and enterprise finance teams. IRS e-file authorized. Bank-level security.
            </p>
            <div className="mt-6 flex items-center gap-3 text-slate-300 text-xs">
              <span className="inline-flex items-center gap-1"><ShieldCheck size={14} /> SOC 2</span>
              <span className="inline-flex items-center gap-1"><LockKey size={14} /> 256-bit TLS</span>
              <span className="inline-flex items-center gap-1"><Certificate size={14} /> IRS e-File</span>
            </div>
            <div className="mt-6 flex items-center gap-2" data-testid="footer-socials">
              {SOCIAL_LINKS.map(({ href, label, Icon, testId }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={label}
                  data-testid={testId}
                  className="h-9 w-9 grid place-items-center rounded-full bg-white/5 hover:bg-white/15 border border-white/10 hover:border-white/30 text-slate-200 hover:text-white transition-colors"
                >
                  <Icon size={16} weight="fill" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-[Chivo] text-sm font-bold uppercase tracking-wider text-white">Services</h4>
            <ul className="mt-5 space-y-3 text-sm text-slate-300">
              <li><Link to="/services/individual" className="hover:text-white">Individual Tax Filing</Link></li>
              <li><Link to="/services/business" className="hover:text-white">Business Tax Filing</Link></li>
              <li><Link to="/services/itin" className="hover:text-white">ITIN Application</Link></li>
              <li><Link to="/services/fbar-fatca" className="hover:text-white">FBAR & FATCA</Link></li>
              <li><Link to="/services/irs-notice" className="hover:text-white">IRS Notice Assistance</Link></li>
              <li><Link to="/services/extensions" className="hover:text-white">Tax Extensions</Link></li>
              <li><Link to="/services/corporate" className="hover:text-white">Corporate Programs</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-[Chivo] text-sm font-bold uppercase tracking-wider text-white">Firm</h4>
            <ul className="mt-5 space-y-3 text-sm text-slate-300">
              <li><Link to="/about" className="hover:text-white">About</Link></li>
              <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
              <li><Link to="/blog" className="hover:text-white">Blog & Insights</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
              <li><Link to="/auth?tab=login" className="hover:text-white">Client Portal Login</Link></li>
              <li><Link to="/auth?tab=register" className="hover:text-white">Open a Client Account</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-[Chivo] text-sm font-bold uppercase tracking-wider text-white">Contact</h4>
            <ul className="mt-5 space-y-3 text-sm text-slate-300">
              <li><a href="https://taxfreefiler.com" target="_blank" rel="noreferrer" className="hover:text-white" data-testid="footer-external-site">taxfreefiler.com ↗</a></li>
              <li><a href="mailto:info@taxfreefiler.com" className="hover:text-white" data-testid="footer-email">info@taxfreefiler.com</a></li>
              <li><a href="tel:+917386724363" className="hover:text-white" data-testid="footer-phone">+91 (738) 672-4363</a></li>
              <li>
                <a
                  href="https://whatsapp.com/channel/0029VbCnQzl4NVilEQdRqq17"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1.5 hover:text-white"
                  data-testid="footer-whatsapp-link"
                >
                  <WhatsappLogo size={14} weight="fill" className="text-emerald-400" /> Follow our WhatsApp channel
                </a>
              </li>
              <li>Mon – Sat · 8am – 8pm IST</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-slate-400">
          <p>© {year} Taxfree Filer LLP. All rights reserved. Not affiliated with the IRS.</p>
          <div className="flex gap-6">
            <Link to="/legal/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-white">Terms</Link>
            <Link to="/legal/security" className="hover:text-white">Security</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
